/**
 * Map the on-screen plate guide (object-cover video) to native video pixels and crop.
 * Exported for unit testing via the same math in backend tests (mirrored constants).
 */

/** Default guide box as fraction of the video container (centered). */
export const PLATE_GUIDE_FRACTION = {
  width: 0.82,
  height: 0.22,
  centerY: 0.48,
};

/**
 * object-cover layout: scale video so it fills container; compute offsets.
 */
export function objectCoverLayout(videoW, videoH, containerW, containerH) {
  if (!videoW || !videoH || !containerW || !containerH) {
    return { scale: 1, offsetX: 0, offsetY: 0, renderedW: videoW, renderedH: videoH };
  }
  const videoAspect = videoW / videoH;
  const containerAspect = containerW / containerH;
  let scale;
  let renderedW;
  let renderedH;
  let offsetX;
  let offsetY;
  if (videoAspect > containerAspect) {
    scale = containerH / videoH;
    renderedW = videoW * scale;
    renderedH = containerH;
    offsetX = (containerW - renderedW) / 2;
    offsetY = 0;
  } else {
    scale = containerW / videoW;
    renderedW = containerW;
    renderedH = videoH * scale;
    offsetX = 0;
    offsetY = (containerH - renderedH) / 2;
  }
  return { scale, offsetX, offsetY, renderedW, renderedH };
}

/**
 * Guide rectangle in container pixel coordinates.
 */
export function plateGuideRectInContainer(containerW, containerH, fraction = PLATE_GUIDE_FRACTION) {
  const gw = containerW * fraction.width;
  const gh = containerH * fraction.height;
  const gx = (containerW - gw) / 2;
  const gy = containerH * fraction.centerY - gh / 2;
  return { x: gx, y: gy, width: gw, height: gh };
}

/**
 * Convert container-space guide rect to source video pixel crop (integers, clamped).
 */
export function mapGuideRectToVideoPixels(videoW, videoH, containerW, containerH, fraction = PLATE_GUIDE_FRACTION) {
  const guide = plateGuideRectInContainer(containerW, containerH, fraction);
  const { scale, offsetX, offsetY } = objectCoverLayout(videoW, videoH, containerW, containerH);
  const x1 = (guide.x - offsetX) / scale;
  const y1 = (guide.y - offsetY) / scale;
  const x2 = (guide.x + guide.width - offsetX) / scale;
  const y2 = (guide.y + guide.height - offsetY) / scale;
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const left = clamp(Math.floor(Math.min(x1, x2)), 0, videoW - 1);
  const top = clamp(Math.floor(Math.min(y1, y2)), 0, videoH - 1);
  const right = clamp(Math.ceil(Math.max(x1, x2)), left + 1, videoW);
  const bottom = clamp(Math.ceil(Math.max(y1, y2)), top + 1, videoH);
  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
}

/**
 * Capture ROI JPEG from live video (full native resolution crop, no aspect distortion).
 */
export function capturePlateRoiFromVideo(videoEl, containerEl, quality = 0.92, fraction = PLATE_GUIDE_FRACTION) {
  if (!videoEl || videoEl.readyState < 2 || !videoEl.videoWidth) return null;
  const videoW = videoEl.videoWidth;
  const videoH = videoEl.videoHeight;
  const containerW = containerEl?.clientWidth || videoW;
  const containerH = containerEl?.clientHeight || videoH;
  const crop = mapGuideRectToVideoPixels(videoW, videoH, containerW, containerH, fraction);
  if (crop.width < 8 || crop.height < 8) return null;
  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    videoEl,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height,
  );
  return canvas.toDataURL('image/jpeg', quality);
}
